<template>
  <section class="TicketsMatch mt-3 p-3 border rounded position-relative">
    <div class="d-flex mb-4">
      <div class="flex-grow" :class="{ submitted: isSubmittedForCurrentType }">
        <Headline level="2" size="4" class="mb-1 text-primary text-uppercase">{{ match.title }}</Headline>
        <p>{{ matchday }}</p>
      </div>
      <div class="pl-3 pt-1 font-weight-bold total" :class="{ submitted: isSubmittedForCurrentType }" v-if="match.total && !match.submitRequest">
        <span v-if="isComplimentaryMode" class="mr-1">Total tickets:</span>
        <span v-if="isComplimentaryMode">{{ match.total || 0 }}</span>
        <span v-else>{{ match.total.toFixed(2) }}</span>
        <span v-if="!isComplimentaryMode" class="ml-1">{{ match.currency }}</span>
      </div>
      <div class="pl-3">
        <pulse-loader
          :loading="Boolean(match.submitRequest)"
          :color="loaderColor"
          v-if="match.submitRequest"
        />
        <b-icon-check-circle
          variant="primary"
          class="icon-submitted"
          v-else-if="isSubmittedForCurrentType"
        />
        <Button
          class="button-delete"
          @click="$emit('removeMatch', match)"
          icon="BIN_OUTLINE"
          type="round"
          v-else
        />
      </div>
    </div>

    <b-row class="TicketsMatch__details-row align-items-xl-start" :class="{ submitted: isSubmittedForCurrentType }">
      <b-col xl="3" class="TicketsMatch__date-venue-col">
        <b-row cols="2" class="TicketsMatch__date-venue-row">
          <b-col :class="columnClass">
            <label :class="[labelClass, 'TicketsMatch__field-label']">Match Date</label>
            <p class="TicketsMatch__field-value mb-0">{{ getMatchKickoffDate(match.startDateTime) }}</p>
          </b-col>
          <b-col :class="columnClass">
            <label :class="[labelClass, 'TicketsMatch__field-label']">Venue</label>
            <p class="TicketsMatch__field-value TicketsMatch__venue mb-0">
              {{ match.venue ? match.venue.localTitle : '' }}
            </p>
          </b-col>
        </b-row>
      </b-col>
      <b-col cols="12" xl="4" :class="[separatorClass, 'TicketsMatch__contacts-col']" v-if="!showLoader">
        <b-row>
          <b-col cols="12" md="6" v-for="type in contactTypes" :key="type.key" :class="columnClass">
            <label :class="[labelClass, 'TicketsMatch__field-label']">{{ type.label }}</label>
            <div v-if="request[type.key]">

              <div v-if="contactsList[type.key] && contactsList[type.key].length"
                   class="d-flex justify-content-between align-items-center">
                <div class="flex-grow-1">
                  <VueSelect
                      v-model="match[type.key]"
                      :options="contactsList[type.key]"
                      :get-option-label="contact => `${contact.firstname} ${contact.lastname}`"
                      :reduce="contact => contact"
                      :disabled="isSubmittedForCurrentType"
                      :clearable="false"
                  />
                </div>
                <div class="ml-3 d-flex align-items-center contact-eye-container">
                  <b-icon-eye
                      variant="primary"
                      :id="'contactTooltip' + match.matchId + type.key"
                      class="pointer"
                      @click="$root.$emit('bv::hide::tooltip')"
                  />
                  <TicketsContactTooltip
                      :target="'contactTooltip' + match.matchId + type.key"
                      :contact="match[type.key]"
                      triggers="click"
                      placement="bottom"
                      variant="primary"
                  />
                </div>
              </div>

            </div>

            <router-link :to="{name: type.route}" class="border border-danger rounded px-2 py-1 text-danger noBr d-inline-block d-xl-inline" v-else>
              Contact missing
            </router-link>

          </b-col>
        </b-row>
      </b-col>
      <b-col
        cols="12"
        xl="5"
        :class="[separatorClass, 'TicketsMatch__zones-col']"
        v-if="!showLoader"
      >
        <div class="TicketsMatch__zone-grid mb-xl-n2" v-if="match.zones && match.zones.length">
          <div
            v-for="zone in match.zones"
            :key="zone.ticketZoneId"
            :class="[columnClass, 'TicketsMatch__zone-col']"
          >
            <label :class="[labelClass, 'text-primary', 'TicketsMatch__field-label', 'TicketsMatch__zone-label']" :title="zone.name">
              {{ zone.name }}
            </label>
            <div class="TicketsMatch__zone-controls">
              <div class="TicketsMatch__zone-input-col">
                <Input
                  :value="zone.quantity"
                  type="number"
                  min="0"
                  :max="(isComplimentaryMode && match.hasComplimentaryQuota) ? (zone.quota || 0) : 10000"
                  step="1"
                  class="m-0"
                  :inputClass="{ 'px-2': true, 'py-1': true, 'border-danger': (isComplimentaryMode && match.hasComplimentaryQuota && zone.quantity > (zone.quota || 0)) }"
                  :disableInput="isSubmittedForCurrentType"
                  @input="updateZoneQuantity(zone, $event)"
                />
              </div>
              <div
                v-if="isComplimentaryMode && match.hasComplimentaryQuota"
                class="TicketsMatch__zone-meta"
              >
                <span class="mx-1">/</span>
                <span>{{ zone.quota || 0 }}</span>
              </div>
              <div v-else-if="!isComplimentaryMode" class="TicketsMatch__zone-meta">
                <span class="mx-1">{{ zone.price ? zone.price.toFixed(2) : '' }}</span>
                <span>{{ zone.currency || match.currency }}</span>
              </div>
            </div>
          </div>
        </div>
        <div class="text-center text-danger font-weight-bold my-4 my-xl-2" v-else>
          Sorry, no tickets available
        </div>
      </b-col>
      <b-col xl="9" :class="separatorClass" class="d-flex justify-content-center align-items-center py-4 py-xl-0" v-if="showLoader">
        <pulse-loader
          :loading="showLoader"
          :color="loaderColor"
        />
      </b-col>
    </b-row>
  </section>
</template>

<script>
import get from 'lodash.get';
import {DtoApiGetComplimentaryTicketsRequest} from "@/classes/dto/api/ticket/dto.api.get-complimentary-tickets.request";
import {DtoApiGetRegularTicketsRequest} from "@/classes/dto/api/ticket/dto.api.get-regular-tickets.request";
import Headline from "@/components/Headline/Headline.component.vue";
import Input from "@/components/Inputs/Input.component.vue";
import moment from "moment/moment";
import Button from "@/components/Button/Button.component.vue";
import RouterNames from "@/router/route.names";
import TicketsContactTooltip from "@/components/Tickets/contacts/TicketsContactTooltip.component.vue";
import PulseLoader from "vue-spinner/src/PulseLoader.vue";
import VueSelect from 'vue-select';
import 'vue-select/dist/vue-select.css';

export default {
  components: {
    Button,
    Input,
    Headline,
    TicketsContactTooltip,
    PulseLoader,
    VueSelect,
  },
  data(){
    return {
      request: null,
      contactTypes: [
        {
          key: 'mainContact',
          label: 'Main Contact',
          route: RouterNames.IndexRoutes.DashboardRoutes.TicketRequestRoutes.MainContacts,
        },
        {
          key: 'deliveryContact',
          label: 'Delivery Contact',
          route: RouterNames.IndexRoutes.DashboardRoutes.TicketRequestRoutes.DeliveryAddress,
        },
      ],
      columnClass: 'py-4 py-xl-2',
      labelClass: 'd-block font-weight-bold mb-3',
      separatorClass: 'separator-border mx-2 mx-xl-0 px-2 px-xl-3',
      loaderColor: '#00A9A0',
      showLoader: true,
      contactsList: {
        mainContact: null,
        deliveryContact: null,
      },
    };
  },
  props: ['match', 'ticketsType'],
  computed: {
    organizationId() {
      return get(this, '$store.state.user.account.id');
    },
    isValid(){
      return (0 === this.match.zones.filter(item => item.quantity > item.quota).length);
    },
    isComplimentaryMode(){
      return (this.ticketsType === 'complimentary');
    },
    isSubmittedForCurrentType() {
      if (this.match.submittedTypes) {
        return Boolean(this.match.submittedTypes[this.ticketsType]);
      }

      return Boolean(this.match.submitted);
    },
    matchday(){
      return [
        this.match?.matchday?.stage?.name,
        this.match?.matchday?.name,
      ].filter(text => text).join(' - ');
    },
  },
  watch: {
    match(){
      this.init()
    },
    ticketsType(){
      this.request = this.initRequest();
      this.init();
    },
  },
  created(){
    this.request = this.initRequest();
    this.init()
  },
  methods: {
    initRequest(){
      if(this.isComplimentaryMode){
        return new DtoApiGetComplimentaryTicketsRequest();
      }
      return new DtoApiGetRegularTicketsRequest();
    },
    async init() {
      const {matchId} = this.match;
      const {organizationId} = this;
      const zoneQuantities = Object.fromEntries((this.match.zones || []).map(zone => [zone.ticketZoneId, zone.quantity]));

      this.showLoader = true;

      this.match.zones = [];
      this.request = await this.request.$get(organizationId, matchId);

      for (const type of this.contactTypes) {
        this.contactsList[type.key] = this.request[type.key] || [];
        if (this.contactsList[type.key].length) {
          this.$set(this.match, type.key, this.contactsList[type.key][0])
        }
      }

      this.match.hasComplimentaryQuota = this.request.organization?.hasComplimentaryQuota;

      if (this.request.availableTickets && this.request.availableTickets.length) {
        this.match.zones = this.mapAvailableTicketsToZones(
          this.request.availableTickets,
          zoneQuantities,
        );
      }

      this.calculateTotal();

      this.showLoader = false;
    },
    mapAvailableTicketsToZones(availableTickets, zoneQuantities) {
      const zonesById = {};
      const zoneOrder = [];

      availableTickets.forEach((ticket) => {
        const ticketZoneId = Number(ticket.ticketZoneId);
        const currency = ticket.currency || null;
        const mappedZone = {
          name: ticket.name,
          quota: ticket.quota,
          price: Number(ticket.price),
          currency,
          quantity: Number(zoneQuantities[ticketZoneId]) || 0,
          ticketZoneId,
        };

        const existingZone = zonesById[ticketZoneId];
        if (!existingZone) {
          zonesById[ticketZoneId] = mappedZone;
          zoneOrder.push(ticketZoneId);
          return;
        }

        if (!existingZone.currency && currency) {
          zonesById[ticketZoneId] = mappedZone;
        }
      });

      const zones = zoneOrder.map((ticketZoneId) => zonesById[ticketZoneId]);

      const matchCurrency = zones.find(zone => zone.currency)?.currency;
      if (matchCurrency) {
        this.match.currency = matchCurrency;
      }

      return zones;
    },
    getMatchKickoffDate(startDateTime) {
      return moment(startDateTime).add(8, 'hours').format('DD.MM.YYYY');
    },
    updateZoneQuantity(zone, quantity) {
      if(!zone){
        return;
      }

      //for some reason chrome gets an Event here while firefox get the new value so conversion is needed
      if(quantity instanceof Event) {
        quantity = quantity.target?.value;
      }

      this.$set(zone, 'quantity', parseInt(quantity, 10) || 0);

      this.calculateTotal();
    },
    calculateTotal(){
      if(this.isComplimentaryMode){
        this.$set(this.match, 'total', this.match.zones.reduce((sum, zone) => sum + Number(zone.quantity), 0));
      }
      else {
        this.$set(this.match, 'total', this.match.zones.reduce((sum, zone) => sum + zone.price * Number(zone.quantity), 0));
      }

      this.$emit('update');
    },
  }
}
</script>

<style lang="scss">
  @import "../../styles/main.scss";
  @import '~bootstrap/scss/functions';
  @import '~bootstrap/scss/variables';
  @import '~bootstrap/scss/mixins/breakpoints';

  .separator-border {
    border-top-width: 1px;
    border-top-style: solid;

    @include media-breakpoint-up(xl) {
      border-top-style: none;
      border-left-width: 1px;
      border-left-style: solid;
    }
  }

  .contact-eye-container {
    margin-top: -9px;
    flex-shrink: 0;
  }

  .button-delete {
    height: auto !important;
    background: none;

    .button-component__icon {
      color: $danger-color !important;
      transform: none;
    }
  }

  .icon-submitted {
    font-size: 1.5rem;
    vertical-align: bottom !important;
  }

  .total {
    font-size: 14px;
  }

  .submitted {
    opacity: 0.5;
  }

  .noBr {
    white-space: nowrap;
  }

  .pointer {
    cursor: pointer;
  }

  .flex-grow {
    flex-grow: 1;
  }

  .TicketsMatch {
    max-width: 100%;
    box-sizing: border-box;
    overflow-x: hidden;
  }

  .TicketsMatch__details-row {
    margin-left: 0;
    margin-right: 0;

    @include media-breakpoint-up(xl) {
      align-items: flex-start;
    }
  }

  // Viewport may be >=1200 (Bootstrap xl) while content area is ~965px at 1280×800 (255px nav + padding).
  @media (min-width: 1200px) and (max-width: 1399.98px) {
    .TicketsMatch__details-row {
      > .TicketsMatch__date-venue-col,
      > .TicketsMatch__contacts-col,
      > .TicketsMatch__zones-col {
        flex: 0 0 100%;
        max-width: 100%;
      }

      .separator-border {
        border-left-style: none;
        border-top-width: 1px;
        border-top-style: solid;
      }
    }

    .TicketsMatch__zone-grid {
      grid-template-columns: repeat(4, minmax(0, 1fr));
    }
  }

  .TicketsMatch__date-venue-col {
    min-width: 0;
  }

  .TicketsMatch__date-venue-row > [class*='col-'] {
    min-width: 0;
  }

  .TicketsMatch__venue {
    overflow-wrap: break-word;
    word-break: break-word;
  }

  .TicketsMatch__zones-col {
    min-width: 0;
    max-width: 100%;
    border-top-width: 1px;
    border-top-style: solid;

    @include media-breakpoint-up(xl) {
      border-top-style: none;
    }
  }

  @media (min-width: 1200px) and (max-width: 1399.98px) {
    .TicketsMatch__zones-col {
      border-top-style: solid;
    }
  }

  .TicketsMatch__zone-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.5rem 0.75rem;
    align-items: stretch;
    min-width: 0;
    width: 100%;

    @include media-breakpoint-up(xl) {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }
  }

  .TicketsMatch__zone-col {
    min-width: 0;
  }

  .TicketsMatch__zone-label {
    overflow-wrap: break-word;
    word-break: break-word;
  }

  .TicketsMatch__zone-controls {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.25rem 0.5rem;
    min-width: 0;
    max-width: 100%;
  }

  @include media-breakpoint-up(md) {
    .TicketsMatch__contacts-col .vs__dropdown-toggle,
    .TicketsMatch__zone-input-col .Input__input {
      min-height: 30px;
    }
  }

  .TicketsMatch__zone-input-col {
    flex: 0 0 auto;

    .form-group {
      margin-bottom: 0;
    }

    .Input__input {
      width: 3.5rem;
      max-width: 100%;
      padding: 4px 8px;
      box-sizing: border-box;
    }
  }

  .TicketsMatch__zone-meta {
    flex: 1 1 auto;
    min-width: 0;
    white-space: normal;
    overflow-wrap: anywhere;
    line-height: 1.2;
    font-size: 11px;
  }

  .vs__dropdown-toggle {
    border: 1px solid #D8DAE3;
    border-radius: 12px;
    font-weight: 700;
    padding: 7px 10px 7px 10px;
    background: #fff;
    min-height: 22px !important;
    padding-top: 0px !important;
    padding-bottom: 0px !important;
    font-size: 10px !important;
    padding-left: 2px !important;
    max-width: 100%
  }
  .vs__selected,
  .vs__search,
  .vs__search::placeholder {
    font-size: 10px !important;
    padding-top: 0px !important;
    padding-bottom: 2px !important;
  }

  .vs__search, .vs__search::placeholder {
    font-weight: 600 !important;
    color: #AFB1BC;
    font-size: 12px;
    padding-left: 5px !important;
    padding-top: 4px !important;
  }

  .vs__selected {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
  }

  .vs__selected-options {
    display: flex;
    flex-wrap: nowrap;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    min-width: 0;
    max-width: 100%;
    align-items: center;
  }

  .vs__dropdown-menu {
    font-size: 12px;
    font-weight: 600 !important;
    border-radius: 0 0 12px 12px;
    border-top: none;
    margin-top: -1px;
  }

  .vs__dropdown-option {
    padding-left: 10px;
    font-size: 12px;
    font-weight: 600 !important;
  }

  .vs__dropdown-option--highlight {
    background-color: $primary-color !important;
    color: #fff !important;
    font-weight: 700 !important;
  }

  .vs__dropdown-option--selected {
    background-color: #d8dae3 !important;
    color: #35495e !important;
  }

  .vs__clear, .vs__open-indicator {
    top: 2px;
  }

  .vs__actions {
    cursor: pointer;
    svg {
      display: none !important;
    }
    &::after {
      content: '';
      display: inline-block;
      margin-left: 2px;
      vertical-align: middle;
      width: 0;
      height: 0;
      border-left: 5px solid transparent;
      border-right: 5px solid transparent;
      border-top: 6px solid #999;
    }
  }

  .vs--disabled {
    background: transparent;
    opacity: 0.5;
  }

  .flex-grow-1 {
    min-width: 0;
  }
</style>
